import api from './api';

// ─── helpers ────────────────────────────────────────────────────
const isCapacitor = () => typeof window !== 'undefined' && !!window.Capacitor;
const isCordova   = () => typeof window !== 'undefined' && !!window.cordova;
const isNative    = () => isCapacitor() || isCordova();

// ─── platform helpers ────────────────────────────────────────────
export const isIos = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

export const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;

// ─── native (Capacitor / Cordova) flow ──────────────────────────
async function registerNativePush() {
  // Access the plugin via the global runtime bridge — no npm import needed.
  // In a Capacitor project the plugin is registered at window.Capacitor.Plugins.
  const PushNotifications =
    window.Capacitor?.Plugins?.PushNotifications ??
    window.cordova?.plugins?.pushNotification;

  if (!PushNotifications) {
    console.warn('[Push] Native push plugin not available');
    return false;
  }

  // Capacitor API
  if (PushNotifications.checkPermissions) {
    let permStatus = await PushNotifications.checkPermissions();
    if (permStatus.receive === 'prompt') {
      permStatus = await PushNotifications.requestPermissions();
    }
    if (permStatus.receive !== 'granted') {
      console.warn('[Push] Native permission denied');
      return false;
    }

    return new Promise((resolve) => {
      PushNotifications.addListener('registration', async ({ value: token }) => {
        try {
          const platform = window.Capacitor?.getPlatform?.() || 'native';
          await api.post('/push/subscribe', { token, platform });
          console.log(`[Push] Native token registered (${platform})`);
          resolve(true);
        } catch (e) {
          console.error('[Push] Native token POST failed:', e);
          resolve(false);
        }
      });

      PushNotifications.addListener('registrationError', (err) => {
        console.error('[Push] Native registration error:', err);
        resolve(false);
      });

      PushNotifications.register();
    });
  }

  console.warn('[Push] Unrecognised native push plugin API');
  return false;
}

// Generate / retrieve a stable ID for this specific browser instance.
// Persists in localStorage so token refreshes on the same browser reuse it.
function getDeviceId() {
  const KEY = 'csm_device_id';
  let id = localStorage.getItem(KEY);
  if (!id) {
    id = 'dev_' + Math.random().toString(36).slice(2, 18);
    localStorage.setItem(KEY, id);
  }
  return id;
}

// ─── web (Firebase) flow ────────────────────────────────────────
const VAPID_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

async function registerWebPush() {
  // iOS requires the app to be installed to the Home Screen before Web Push works.
  if (isIos() && !isStandalone()) {
    console.warn('[Push] iOS: must be installed as PWA (Add to Home Screen)');
    return 'ios-not-installed';
  }

  if (!('Notification' in window) || !('serviceWorker' in navigator)) {
    console.warn('[Push] Web push not supported');
    return 'not-supported';
  }

  const { messagingInstance, getToken } = await import('./firebase');
  if (!messagingInstance) {
    console.warn('[Push] Firebase messaging not available');
    return false;
  }

  let permission = Notification.permission;
  if (permission === 'default') {
    permission = await Notification.requestPermission();
  }
  if (permission !== 'granted') {
    console.warn('[Push] Permission denied');
    return 'permission-denied';
  }

  // getRegistration(url) matches ANY sw whose scope covers that url — which
  // means service-worker.js (scope '/') would be returned instead of the
  // Firebase-specific one. Use getRegistrations() and filter by script URL.
  const registrations = await navigator.serviceWorker.getRegistrations();
  let registration = registrations.find(r =>
    r.active?.scriptURL?.includes('firebase-messaging-sw.js') ||
    r.installing?.scriptURL?.includes('firebase-messaging-sw.js') ||
    r.waiting?.scriptURL?.includes('firebase-messaging-sw.js')
  );
  if (!registration) {
    registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    // Wait for the new SW to become active before requesting the FCM token
    await new Promise(resolve => {
      if (registration.active) { resolve(); return; }
      const sw = registration.installing || registration.waiting;
      if (sw) {
        sw.addEventListener('statechange', function handler() {
          if (this.state === 'activated') { sw.removeEventListener('statechange', handler); resolve(); }
        });
      } else {
        navigator.serviceWorker.ready.then(resolve);
      }
    });
  }

  const token = await getToken(messagingInstance, {
    vapidKey: VAPID_KEY,
    serviceWorkerRegistration: registration,
  });
  if (!token) {
    console.warn('[Push] No token received');
    return 'no-token';
  }

  await api.post('/push/subscribe', { token, platform: 'web', device_id: getDeviceId() });
  console.log('[Push] Web token registered');
  return true;
}

// ─── public API ─────────────────────────────────────────────────

/**
 * Register for push notifications.
 * Detects native container (Capacitor/Cordova) vs browser and uses
 * the appropriate push channel.  Returns true on success.
 */
export async function registerPush() {
  try {
    if (isNative()) return await registerNativePush();
    return await registerWebPush();
  } catch (e) {
    console.error('[Push] Registration failed:', e);
    return false;
  }
}

/**
 * Listen for foreground messages (web only).
 * In a native container this is a no-op; Capacitor delivers via its
 * own `pushNotificationReceived` listener set up in the native project.
 */
export function onForegroundMessage(callback) {
  if (isNative()) return () => {};
  try {
    import('./firebase').then(({ messagingInstance, onMessage }) => {
      if (messagingInstance) {
        onMessage(messagingInstance, (payload) => callback(payload));
      }
    });
  } catch { /* ignore */ }
  return () => {};
}
